import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Input,
    Select,
    Space,
    Tag,
    Typography,
    Card,
    Pagination,
    Modal,
    message,
    Tooltip,
    theme,
    Row,
    Col,
    Avatar,
    Grid,
    List,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    UserOutlined,
    ShopOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    StopOutlined,
    QuestionCircleOutlined,
    MoreOutlined,
} from '@ant-design/icons';
import { Contractor, ContractorFilters, ContractorStatus } from '../types/contractor';
import { crmApi } from '../../../api/client';
import { ContractorForm } from './ContractorForm';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

export const ContractorsList: React.FC = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();
    const [contractors, setContractors] = useState<Contractor[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<ContractorFilters>({
        page: 1,
        limit: 10,
        search: '',
        status: undefined
    });
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedContractor, setSelectedContractor] = useState<Contractor | undefined>(undefined);

    const navigate = useNavigate();

    const fetchContractors = async () => {
        setLoading(true);
        try {
            const response = await crmApi.getContractors(filters);
            setContractors(response.data.data);
            setTotal(response.data.total);
        } catch (error) {
            console.error('Failed to fetch contractors', error);
            message.error('Nie udało się pobrać listy kontrahentów');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContractors();
    }, [filters]);

    const handleFilterChange = (key: keyof ContractorFilters, value: any) => {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    const handleAdd = () => {
        setSelectedContractor(undefined);
        setIsFormOpen(true);
    };

    const handleEdit = (event: React.MouseEvent | React.TouchEvent, contractor: Contractor) => {
        event.stopPropagation();
        setSelectedContractor(contractor);
        setIsFormOpen(true);
    };

    const handleDelete = async (event: React.MouseEvent | React.TouchEvent, id: string) => {
        event.stopPropagation();
        Modal.confirm({
            title: 'Czy na pewno chcesz usunąć tego kontrahenta?',
            content: 'Tej operacji nie można cofnąć.',
            okText: 'Tak, usuń',
            okType: 'danger',
            cancelText: 'Anuluj',
            onOk: async () => {
                try {
                    await crmApi.deleteContractor(id);
                    message.success('Kontrahent został usunięty');
                    fetchContractors();
                } catch (error) {
                    console.error('Failed to delete contractor', error);
                    message.error('Nie udało się usunąć kontrahenta');
                }
            }
        });
    };

    const handleFormSubmit = async () => {
        setIsFormOpen(false);
        fetchContractors();
    };

    const getStatusConfig = (status: ContractorStatus) => {
        switch (status) {
            case ContractorStatus.ACTIVE: return { color: 'success', icon: <CheckCircleOutlined />, label: 'Aktywny' };
            case ContractorStatus.INACTIVE: return { color: 'error', icon: <StopOutlined />, label: 'Nieaktywny' };
            case ContractorStatus.POTENTIAL: return { color: 'processing', icon: <QuestionCircleOutlined />, label: 'Potencjalny' };
            case ContractorStatus.BLACKLIST: return { color: 'default', icon: <StopOutlined />, label: 'Czarna lista' };
            default: return { color: 'default', icon: null, label: status };
        }
    };

    const columns = [
        {
            title: 'Nazwa',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: Contractor) => (
                <Space>
                    <Avatar shape="square" icon={<ShopOutlined />} style={{ backgroundColor: token.colorPrimaryBg, color: token.colorPrimary }} />
                    <Text strong>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'NIP',
            dataIndex: 'nip',
            key: 'nip',
            render: (text: string) => <Text type="secondary">{text || '-'}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: ContractorStatus) => {
                const config = getStatusConfig(status);
                return <Tag icon={config.icon} color={config.color}>{config.label}</Tag>;
            },
        },
        {
            title: 'Opiekun',
            dataIndex: 'accountManager',
            key: 'accountManager',
            render: (manager: any) => manager ? (
                <Space>
                    <Avatar size="small" icon={<UserOutlined />} src={manager.avatarUrl} />
                    <span>{manager.firstName} {manager.lastName}</span>
                </Space>
            ) : <Text type="secondary">-</Text>,
        },
        {
            title: 'Ostatnie zamówienie',
            dataIndex: 'lastOrderDate',
            key: 'lastOrderDate',
            render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : <Text type="secondary">-</Text>,
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 100,
            render: (_: any, record: Contractor) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edytuj">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={(e) => handleEdit(e, record)}
                        />
                    </Tooltip>
                    <Tooltip title="Usuń">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => handleDelete(e, record.id)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="Kontrahenci B2B"
            extra={!isMobile && (
                <Space>
                    <Input
                        placeholder="Szukaj..."
                        prefix={<SearchOutlined />}
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        style={{ width: 250 }}
                        allowClear
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Dodaj kontrahenta
                    </Button>
                </Space>
            )}
        >
            {isMobile && (
                <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                    <Input
                        placeholder="Szukaj..."
                        prefix={<SearchOutlined />}
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        allowClear
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Select
                            placeholder="Wszystkie statusy"
                            style={{ flex: 1 }}
                            value={filters.status}
                            onChange={(value) => handleFilterChange('status', value)}
                            allowClear
                            options={[
                                { value: ContractorStatus.ACTIVE, label: 'Aktywny' },
                                { value: ContractorStatus.POTENTIAL, label: 'Potencjalny' },
                                { value: ContractorStatus.INACTIVE, label: 'Nieaktywny' },
                                { value: ContractorStatus.BLACKLIST, label: 'Czarna lista' },
                            ]}
                        />
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            Dodaj
                        </Button>
                    </div>
                </Space>
            )}
            {!isMobile && (
                <div style={{ marginBottom: 16 }}>
                    <Space>
                        <span style={{ marginRight: 8 }}>Status:</span>
                        <Select
                            placeholder="Wszystkie statusy"
                            style={{ width: 200 }}
                            value={filters.status}
                            onChange={(value) => handleFilterChange('status', value)}
                            allowClear
                            options={[
                                { value: ContractorStatus.ACTIVE, label: 'Aktywny' },
                                { value: ContractorStatus.POTENTIAL, label: 'Potencjalny' },
                                { value: ContractorStatus.INACTIVE, label: 'Nieaktywny' },
                                { value: ContractorStatus.BLACKLIST, label: 'Czarna lista' },
                            ]}
                        />
                    </Space>
                </div>
            )}

            {isMobile ? (
                <List
                    dataSource={contractors}
                    loading={loading}
                    renderItem={(record) => {
                        const config = getStatusConfig(record.status);
                        return (
                            <List.Item style={{ padding: '8px 0', border: 'none' }}>
                                <Card
                                    size="small"
                                    onClick={() => navigate(`/b2b/${record.id}`)}
                                    style={{ width: '100%', cursor: 'pointer', borderRadius: 8 }}
                                    actions={[
                                        <Button type="text" icon={<EditOutlined />} onClick={(e) => handleEdit(e, record)}>Edytuj</Button>,
                                        <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => handleDelete(e, record.id)}>Usuń</Button>
                                    ]}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Space align="start">
                                            <Avatar shape="square" icon={<ShopOutlined />} style={{ backgroundColor: token.colorPrimaryBg, color: token.colorPrimary }} />
                                            <div>
                                                <div style={{ fontWeight: 600 }}>{record.name}</div>
                                                <Text type="secondary" style={{ fontSize: 12 }}>NIP: {record.nip || '-'}</Text>
                                            </div>
                                        </Space>
                                        <Tag icon={config.icon} color={config.color} style={{ margin: 0 }}>{config.label}</Tag>
                                    </div>
                                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between' }}>
                                        <div style={{ fontSize: 12 }}>
                                            <Text type="secondary">Opiekun:</Text> {record.accountManager ? `${record.accountManager.firstName} ${record.accountManager.lastName}` : '-'}
                                        </div>
                                        <div style={{ fontSize: 12 }}>
                                            <Text type="secondary">Ostatnie zam.:</Text> {record.lastOrderDate ? dayjs(record.lastOrderDate).format('DD.MM.YYYY') : '-'}
                                        </div>
                                    </div>
                                </Card>
                            </List.Item>
                        );
                    }}
                    pagination={{
                        current: filters.page,
                        pageSize: filters.limit,
                        total: total,
                        onChange: (page, pageSize) => setFilters(prev => ({ ...prev, page, limit: pageSize })),
                        size: 'small',
                        simple: true,
                        style: { textAlign: 'center', marginTop: 16 }
                    }}
                />
            ) : (
                <Table
                    columns={columns}
                    dataSource={contractors}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: filters.page,
                        pageSize: filters.limit,
                        total: total,
                        onChange: (page, pageSize) => setFilters(prev => ({ ...prev, page, limit: pageSize })),
                        showSizeChanger: true,
                        showTotal: (total) => `Łącznie ${total} kontrahentów`
                    }}
                    onRow={(record) => ({
                        onClick: () => navigate(`/b2b/${record.id}`),
                        style: { cursor: 'pointer' }
                    })}
                />
            )}

            <ContractorForm
                open={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                contractor={selectedContractor}
                onSubmit={handleFormSubmit}
            />
        </Card>
    );
};

export default ContractorsList;
